import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Form, Input, Button, Select, InputNumber,
  notification, Row, Col, Typography, Spin,
  Result, Divider, Modal, Slider,
} from 'antd';
import {
  UserOutlined, PhoneOutlined, HomeOutlined,
  EnvironmentOutlined, ShopOutlined, SendOutlined,
  CheckCircleOutlined, CalendarOutlined, AppstoreOutlined,
  CameraOutlined, PlusOutlined, LoadingOutlined, ScissorOutlined,
} from '@ant-design/icons';
import Cropper from 'react-easy-crop';
import { baseUrl } from '../config';

// ── Canvas crop helper (reused from BirthdayForm) ────────────────────────────
const createImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.addEventListener('load', () => resolve(img));
  img.addEventListener('error', reject);
  img.setAttribute('crossOrigin', 'anonymous');
  img.src = url;
});

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image  = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx    = canvas.getContext('2d');
  const maxDim = 900;
  const scale  = Math.min(maxDim / pixelCrop.width, maxDim / pixelCrop.height, 1);
  canvas.width  = Math.round(pixelCrop.width  * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, canvas.width, canvas.height);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
}

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ── Validation helpers ────────────────────────────────────────────────────────
const URL_PATTERN  = /(https?:\/\/|www\.|\.com|\.net|\.org|\.in|\.co|<[^>]+>|javascript:|on\w+=|&lt;|&#|<script|SELECT |INSERT |DROP |DELETE |UPDATE )/i;
const NAME_PATTERN = /^[a-zA-Zऀ-ॿ\s.''\-]+$/;

const validateSafe = (label) => (_, value) => {
  if (!value || !value.trim()) return Promise.resolve();
  if (URL_PATTERN.test(value)) return Promise.reject(`${label} में link, script या HTML की अनुमति नहीं है।`);
  return Promise.resolve();
};

const validateName = (label) => (_, value) => {
  if (!value || !value.trim()) return Promise.reject(`${label} दर्ज करें।`);
  if (URL_PATTERN.test(value)) return Promise.reject('Link या script की अनुमति नहीं है।');
  if (!NAME_PATTERN.test(value.trim())) return Promise.reject(`${label} में केवल अक्षर लिखें।`);
  return Promise.resolve();
};

const phoneRules = [
  { required: true, message: 'मोबाइल नंबर आवश्यक है।' },
  {
    validator: (_, value) => {
      if (!value) return Promise.reject('मोबाइल नंबर दर्ज करें।');
      if (!/^\d+$/.test(value)) return Promise.reject('केवल अंक (0-9) दर्ज करें।');
      if (value.length !== 10) return Promise.reject('मोबाइल नंबर 10 अंकों का होना चाहिए।');
      if (!/^[6-9]/.test(value)) return Promise.reject('मोबाइल नंबर 6, 7, 8 या 9 से शुरू होना चाहिए।');
      return Promise.resolve();
    },
  },
];

function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return mobile;
}

function useSectors() {
  const [sectors, setSectors] = useState([]);
  const [loadingSectors, setLoadingSectors] = useState(true);

  useEffect(() => {
    fetch(`${baseUrl}/business-sector/public-list`)
      .then(r => r.json())
      .then(data => { if (data.status) setSectors(data.result || []); })
      .catch(() => {})
      .finally(() => setLoadingSectors(false));
  }, []);

  return { sectors, loadingSectors };
}

function usePublicConfig() {
  const [cfg, setCfg] = useState({});
  useEffect(() => {
    fetch(`${baseUrl}/business-directory/public-config`)
      .then(r => r.json())
      .then(data => { if (data.status) setCfg(data.result || {}); })
      .catch(() => {});
  }, []);
  return cfg;
}

// ── Photo upload for public form ──────────────────────────────────────────────
const MAX_PUBLIC_KB = 5120; // 5MB — backend compresses to ~150KB via sharp

// ── Upload a blob to server ───────────────────────────────────────────────────
async function doUpload(blob, type) {
  const fd = new FormData();
  fd.append('photo', blob, 'photo.jpg');
  const res  = await fetch(`${baseUrl}/business-directory/upload-photo?type=${type}`, { method: 'POST', body: fd });
  const data = await res.json();
  if (!data.status) throw new Error(data.message || 'अपलोड विफल हुआ।');
  return data.url;
}

// ── Crop Modal ────────────────────────────────────────────────────────────────
function CropModal({ open, imageSrc, aspect, onDone, onCancel }) {
  const [crop, setCrop]               = useState({ x: 0, y: 0 });
  const [zoom, setZoom]               = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [applying, setApplying]       = useState(false);

  const onCropComplete = useCallback((_, pixels) => setCroppedPixels(pixels), []);

  const apply = async () => {
    if (!croppedPixels) return;
    setApplying(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedPixels);
      onDone(blob);
    } catch {
      notification.error({ message: 'Crop विफल हुआ, पुनः प्रयास करें।', placement: 'top' });
    } finally { setApplying(false); }
  };

  return (
    <Modal
      open={open}
      title={<span><ScissorOutlined style={{ marginRight: 8, color: '#7a0000' }} />फोटो काटें और सेट करें</span>}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>रद्द करें</Button>,
        <Button key="apply" type="primary" loading={applying} onClick={apply}
          style={{ background: '#7a0000', borderColor: '#7a0000' }}>
          ✓ सेट करें
        </Button>,
      ]}
      width={360}
      centered
      destroyOnClose
    >
      <div style={{ position: 'relative', height: 280, background: '#111', borderRadius: 8, overflow: 'hidden' }}>
        {imageSrc && (
          <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspect}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
        )}
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>ज़ूम करें</div>
        <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom}
          trackStyle={{ background: '#7a0000' }} handleStyle={{ borderColor: '#7a0000' }} />
      </div>
      <div style={{ fontSize: 11, color: '#aaa', marginTop: 8, textAlign: 'center' }}>
        फोटो को खींचकर और ज़ूम करके सही जगह सेट करें
      </div>
    </Modal>
  );
}

// ── Profile photo with crop ───────────────────────────────────────────────────
const PublicPhotoUpload = ({ value, onChange }) => {
  const [preview, setPreview]     = useState(value || null);
  const [uploading, setUploading] = useState(false);
  const [rawSrc, setRawSrc]       = useState(null);
  const [cropOpen, setCropOpen]   = useState(false);
  const inputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'यह फाइल फोटो नहीं है। कृपया JPG या PNG फोटो ही चुनें।', placement: 'top' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false);
    setUploading(true);
    try {
      const url = await doUpload(blob, 'profile');
      setPreview(url); onChange(url);
    } catch (err) {
      notification.error({ message: 'अपलोड में त्रुटि: ' + err.message, placement: 'top' });
    } finally { setUploading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handleFile(e.target.files[0])} />

      {/* Circle — Facebook style */}
      <div style={{ position: 'relative', width: 86, height: 86 }}>
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ width: 86, height: 86, borderRadius: 12, border: '3px solid #d4a84b', overflow: 'hidden', cursor: 'pointer', background: '#e8e8e8', position: 'relative' }}>
          {preview
            ? <img src={preview} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 10 }} />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                {uploading
                  ? <LoadingOutlined style={{ fontSize: 28, color: '#7a0000' }} />
                  : <UserOutlined style={{ fontSize: 36, color: '#bbb' }} />
                }
              </div>
          }
        </div>

        {/* Camera badge — bottom right */}
        <div onClick={() => !uploading && inputRef.current?.click()}
          style={{ position: 'absolute', bottom: -4, right: -4, width: 26, height: 26, borderRadius: 8, background: '#7a0000', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <CameraOutlined style={{ color: '#fff', fontSize: 12 }} />
        </div>

        {/* Remove */}
        {preview && !uploading && (
          <button type="button"
            onClick={e => { e.stopPropagation(); setPreview(null); onChange(null); if (inputRef.current) inputRef.current.value = ''; }}
            style={{ position: 'absolute', top: 0, right: 0, width: 20, height: 20, borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff', color: '#fff', fontSize: 10, cursor: 'pointer', lineHeight: '16px', textAlign: 'center', padding: 0 }}>
            ✕
          </button>
        )}
      </div>

      <span style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
        {preview ? 'बदलने के लिए tap करें' : 'फोटो · max 5MB'}
      </span>

      <CropModal open={cropOpen} imageSrc={rawSrc} aspect={1}
        onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </div>
  );
};

// ── Horizontal strip for business photos (2 slots) ───────────────────────────
const PublicMultiPhotoUpload = ({ value, onChange }) => {
  const [slots, setSlots]       = useState(() => { const u = value ? value.split(',').filter(Boolean) : []; return [u[0]||null, u[1]||null]; });
  const [uploading, setUploading] = useState([false, false]);
  const [rawSrc, setRawSrc]     = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingIdx, setPendingIdx] = useState(null);
  const refs = [useRef(null), useRef(null)];

  const notify = (s) => onChange(s.filter(Boolean).join(',') || null);

  const handleFile = (file, idx) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      notification.warning({ message: 'यह फाइल फोटो नहीं है। कृपया JPG या PNG फोटो ही चुनें।', placement: 'top' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); setPendingIdx(idx); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (refs[idx].current) refs[idx].current.value = '';
  };

  const onCropDone = async (blob) => {
    setCropOpen(false);
    const idx = pendingIdx;
    setUploading(u => { const n=[...u]; n[idx]=true; return n; });
    try {
      const url = await doUpload(blob, 'business');
      setSlots(s => { const n=[...s]; n[idx]=url; notify(n); return n; });
    } catch (err) {
      notification.error({ message: 'अपलोड में त्रुटि: ' + err.message, placement: 'top' });
    } finally { setUploading(u => { const n=[...u]; n[idx]=false; return n; }); }
  };

  const remove = (idx) => {
    setSlots(s => { const n=[...s]; n[idx]=null; notify(n); return n; });
    if (refs[idx].current) refs[idx].current.value = '';
  };

  return (
    <>
    <div style={{ width: '100%', border: '2px dashed #d4a84b', borderRadius: 12, background: '#fdf9f0', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12, minHeight: 96 }}>
      {/* Hidden inputs */}
      {[0,1].map(idx => (
        <input key={idx} ref={refs[idx]} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files[0], idx)} />
      ))}

      {/* Thumbnails */}
      {[0,1].map(idx => (
        slots[idx] ? (
          <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
            <img src={slots[idx]} alt={`biz-${idx+1}`}
              onClick={() => refs[idx].current?.click()}
              style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '2px solid #d4a84b', cursor: 'pointer', display: 'block' }} />
            <div onClick={() => refs[idx].current?.click()}
              style={{ position: 'absolute', inset: 0, borderRadius: 8, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <CameraOutlined style={{ color: '#fff', fontSize: 16 }} />
            </div>
            <button type="button" onClick={() => remove(idx)}
              style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#ff4d4f', border: '2px solid #fff', color: '#fff', fontSize: 9, cursor: 'pointer', lineHeight: '14px', textAlign: 'center', padding: 0 }}>
              ✕
            </button>
          </div>
        ) : (
          uploading[idx] ? (
            <div key={idx} style={{ width: 72, height: 72, borderRadius: 8, border: '2px dashed #d4a84b', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <LoadingOutlined style={{ fontSize: 20, color: '#7a0000' }} />
            </div>
          ) : (
            <button key={idx} type="button" onClick={() => refs[idx].current?.click()}
              style={{ width: 72, height: 72, borderRadius: 8, border: '2px dashed #d4a84b', background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
              <PlusOutlined style={{ fontSize: 20, color: '#d4a84b' }} />
              <span style={{ fontSize: 10, color: '#7a0000' }}>फोटो {idx+1}</span>
            </button>
          )
        )
      ))}

      {/* Info text */}
      <div style={{ flex: 1, color: '#bbb', fontSize: 11, lineHeight: 1.8 }}>
        दुकान / व्यवसाय की फोटो<br />
        JPG / PNG · max 5MB
      </div>
    </div>

    <CropModal open={cropOpen} imageSrc={rawSrc} aspect={4/3}
      onDone={onCropDone} onCancel={() => setCropOpen(false)} />
    </>
  );
};

const getStyles = (mobile) => ({
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #f5e6c8 0%, #fdf3e3 50%, #f5e6c8 100%)',
    padding: mobile ? '14px 12px 32px' : '28px 16px 40px',
    overflowX: 'hidden',
  },
  wrapper: {
    maxWidth: 680,
    margin: '0 auto',
  },
  headerCard: {
    borderRadius: 14,
    border: 'none',
    background: 'linear-gradient(135deg, #7a0000 0%, #c0392b 50%, #7a0000 100%)',
    marginBottom: 12,
    boxShadow: '0 4px 20px rgba(139,0,0,0.3)',
  },
  formCard: {
    borderRadius: 14,
    border: 'none',
    background: '#ffffff',
    boxShadow: '0 3px 10px rgba(139,0,0,0.12)',
    marginTop: 0,
  },
  input: {
    height: mobile ? 46 : 44,
    fontSize: mobile ? 16 : 14,
    borderRadius: 8,
    borderColor: '#d4a84b',
  },
  select: {
    fontSize: mobile ? 16 : 14,
    borderRadius: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: '#4a2c00',
  },
  submitBtn: {
    height: mobile ? 54 : 50,
    fontSize: mobile ? 17 : 16,
    fontWeight: 700,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #7a0000 0%, #c0392b 100%)',
    border: 'none',
    boxShadow: '0 4px 16px rgba(139,0,0,0.35)',
    letterSpacing: 0.5,
  },
});

function BusinessDirectoryForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [submittedName, setSubmittedName] = useState('');
  const [selectedSector, setSelectedSector] = useState(null);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneDuplicate, setPhoneDuplicate] = useState(false);
  const { sectors, loadingSectors } = useSectors();
  const cfg = usePublicConfig();
  const mobile = useIsMobile();
  const styles = getStyles(mobile);

  const subOptions = selectedSector
    ? (sectors.find(s => String(s.id) === String(selectedSector))?.sub_sectors || [])
    : [];

  const onSectorChange = (val) => {
    setSelectedSector(val);
    form.setFieldsValue({ sub_sector: undefined });
  };

  const onPhoneBlur = async (e) => {
    const phone = e.target.value?.trim();
    if (!/^\d{10}$/.test(phone)) return;
    setPhoneChecking(true);
    setPhoneDuplicate(false);
    try {
      const res = await fetch(`${baseUrl}/business-directory/check-phone?phone_no=${phone}`);
      const data = await res.json();
      if (data.exists) {
        setPhoneDuplicate(true);
        notification.warning({
          message: 'पहले से दर्ज है',
          description: `इस मोबाइल नंबर (${phone}) से पहले ही जानकारी दर्ज हो चुकी है। एक नंबर से केवल एक बार फॉर्म भरा जा सकता है।`,
          placement: 'top',
          duration: 7,
        });
      }
    } catch {}
    finally { setPhoneChecking(false); }
  };

  const onFinish = async (values) => {
    if (phoneDuplicate) {
      notification.warning({
        message: 'पहले से दर्ज है',
        description: 'यह मोबाइल नंबर पहले से दर्ज है। कृपया दूसरा नंबर उपयोग करें।',
        placement: 'top',
      });
      return;
    }
    setLoading(true);
    try {
      const sectorObj   = sectors.find(s => String(s.id) === String(values.sector));
      const sectorLabel = sectorObj?.name || values.sector || '';
      const subLabel    = (sectorObj?.sub_sectors || []).find(s => String(s.id) === String(values.sub_sector))?.name || '';
      const payload = { ...values, sector_label: sectorLabel, sub_sector_label: subLabel };

      const res = await fetch(`${baseUrl}/business-directory/public-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();

      if (result.status) {
        setSubmittedName(values.name);
        setSubmitted(true);
      } else if (result.duplicate) {
        notification.warning({
          message: 'पहले से दर्ज है',
          description: result.message,
          placement: 'top',
          duration: 6,
        });
      } else {
        throw new Error(result.message || 'Submit failed');
      }
    } catch (err) {
      notification.error({
        message: 'जमा करने में त्रुटि',
        description: err.message || 'कृपया पुनः प्रयास करें।',
        placement: 'top',
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Intro / Welcome screen ───────────────────────────────────────────────
  if (showIntro) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #f5e6c8 0%, #fdf3e3 50%, #f5e6c8 100%)',
        padding: mobile ? '16px 12px 32px' : '32px 16px 48px',
      }}>
        <style>{`
          .bd-form .ant-form-item { margin-bottom: ${mobile ? '10px' : '18px'} !important; }
          .bd-form .ant-form-item-label { padding: 0 0 2px !important; }
          .bd-form .ant-form-item-label > label { height: auto !important; font-size: 14px !important; font-weight: 600 !important; color: #4a2c00 !important; }
        `}</style>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>

          {/* Top header strip */}
          <div style={{
            background: 'linear-gradient(135deg, #7a0000 0%, #c0392b 50%, #7a0000 100%)',
            borderRadius: 14,
            padding: mobile ? '14px 16px' : '20px 32px',
            textAlign: 'center',
            marginBottom: 14,
            boxShadow: '0 4px 20px rgba(139,0,0,0.3)',
          }}>
            <Text style={{ color: '#f5d9a0', fontSize: 11, display: 'block' }}>
              ।। ॐ ब्रह्मणे नमः ।। जय गुरु महाराज री सा 🙏
            </Text>
            <Title level={mobile ? 4 : 3} style={{ color: '#FFD700', margin: '4px 0 2px', fontFamily: 'serif', lineHeight: 1.25, fontSize: mobile ? 17 : 24 }}>
              अखिल भारतीय राजपुरोहित समाज विकास संस्थान
            </Title>
            <Text style={{ color: '#f5c842', fontSize: mobile ? 12 : 14, display: 'block', fontWeight: 500 }}>
              श्री खेतेश्वर ब्रह्मधाम तीर्थ, आसोत्रा
            </Text>
          </div>

          {/* Main intro card */}
          <div style={{
            background: '#ffffff',
            borderRadius: 14,
            boxShadow: '0 3px 10px rgba(139,0,0,0.12)',
            overflow: 'hidden',
          }}>
            {/* Kheteshwar image */}
            <div style={{
              background: 'linear-gradient(180deg, #fdf3e3 0%, #f5e6c8 100%)',
              display: 'flex',
              justifyContent: 'center',
              padding: mobile ? '20px 16px 12px' : '28px 32px 16px',
              borderBottom: '1px solid #f0d9a0',
            }}>
              <img
                src="/Shri_Kheteshwar.jpg"
                alt="श्री खेतेश्वर महाराज"
                style={{
                  width: mobile ? 110 : 140,
                  height: mobile ? 110 : 140,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  objectPosition: 'top center',
                  border: '4px solid #7a0000',
                  boxShadow: '0 4px 16px rgba(139,0,0,0.25)',
                }}
              />
            </div>

            {/* Description */}
            <div style={{ padding: mobile ? '16px 16px 20px' : '24px 32px 28px' }}>

              {/* Blessings line */}
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Text style={{ fontSize: mobile ? 12 : 13, color: '#7a0000', fontStyle: 'italic', lineHeight: 1.6 }}>
                  कुलगुरु श्री खेतेश्वर महाराज एवं परम पूज्य श्री तुलछाराम जी महाराज की सत्प्रेरणा
                  तथा वेदांताचार्य श्री ध्यानाराम जी महाराज के मार्गदर्शन में। 🙏
                </Text>
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: '#f0d9a0', margin: '0 0 16px' }} />

              {/* Purpose */}
              <Text style={{ fontSize: mobile ? 13 : 14, color: '#333', lineHeight: 1.75, display: 'block', marginBottom: 14 }}>
                <Text strong style={{ color: '#7a0000' }}>अखिल भारतीय राजपुरोहित समाज विकास संस्थान,
                श्री खेतेश्वर ब्रह्मधाम तीर्थ</Text> द्वारा समाज के व्यवसायियों एवं प्रोफेशनल्स का
                विवरण संकलित किया जा रहा है।
              </Text>

              {/* Purpose bullets */}
              <div style={{ background: '#fdf3e3', borderRadius: 10, padding: mobile ? '12px 14px' : '14px 20px', marginBottom: 16 }}>
                <Text style={{ fontSize: 12, color: '#7a0000', fontWeight: 700, display: 'block', marginBottom: 8 }}>
                  इस जानकारी का उद्देश्य:
                </Text>
                {[
                  'समाज के व्यवसायियों को एक मंच पर लाना।',
                  'आपसी सहयोग एवं व्यावसायिक संपर्क को बढ़ावा देना।',
                  'सामाजिक एवं धार्मिक आयोजनों में समाजबंधुओं की सेवाएँ उपलब्ध कराना।',
                  'नए व्यापारिक अवसर एवं ग्राहकों तक पहुँच बनाना।',
                ].map((point, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: i < 3 ? 6 : 0 }}>
                    <Text style={{ color: '#7a0000', fontSize: 13, flexShrink: 0 }}>•</Text>
                    <Text style={{ fontSize: mobile ? 12 : 13, color: '#444', lineHeight: 1.6 }}>{point}</Text>
                  </div>
                ))}
              </div>

              {/* Appeal */}
              <Text style={{ fontSize: mobile ? 12 : 13, color: '#555', lineHeight: 1.7, display: 'block', marginBottom: 16, textAlign: 'center', fontWeight: 500 }}>
                कृपया सही एवं पूर्ण जानकारी भरकर इस पहल में अपना सहयोग प्रदान करें। 🙏
              </Text>

              {/* Contact */}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <Text style={{ fontSize: 12, color: '#888' }}>
                  यदि कोई प्रश्न हो तो संपर्क करें:
                </Text>
                <br />
                <Text strong style={{ fontSize: mobile ? 15 : 16, color: '#7a0000' }}>
                  📞 9166141008
                </Text>
              </div>

              {/* Start button */}
              <Button
                type="primary"
                block
                size="large"
                onClick={() => setShowIntro(false)}
                style={{
                  height: mobile ? 50 : 48,
                  fontSize: mobile ? 16 : 15,
                  fontWeight: 700,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #7a0000 0%, #c0392b 100%)',
                  border: 'none',
                  boxShadow: '0 4px 16px rgba(139,0,0,0.35)',
                  letterSpacing: 0.3,
                }}
              >
                फॉर्म भरना शुरू करें →
              </Button>

            </div>
          </div>

          {/* Footer */}
          <div style={{ textAlign: 'center', padding: '14px 8px 0' }}>
            <Text style={{ fontSize: 11, color: '#999' }}>
              अखिल भारतीय राजपुरोहित समाज विकास संस्थान — श्री खेतेश्वर ब्रह्मधाम तीर्थ, आसोत्रा
            </Text>
          </div>

        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.wrapper}>
          <Card style={{ ...styles.formCard, borderRadius: 16, marginTop: mobile ? 0 : 0 }}>
            <Result
              status="success"
              icon={<CheckCircleOutlined style={{ color: '#7a0000', fontSize: 64 }} />}
              title={
                <Title level={3} style={{ color: '#7a0000', marginBottom: 8 }}>
                  जय गुरु महाराज री सा! 🙏
                </Title>
              }
              subTitle={
                <div>
                  <Paragraph style={{ fontSize: 16, color: '#555', marginBottom: 8 }}>
                    <Text strong style={{ color: '#7a0000' }}>{submittedName} जी</Text>, आपकी जानकारी सफलतापूर्वक दर्ज हो गई।
                  </Paragraph>
                  <Paragraph style={{ fontSize: 14, color: '#777' }}>
                    आपसे जल्द ही पर संपर्क किया जाएगा।
                  </Paragraph>
                </div>
              }
              extra={[
                <Button
                  key="done"
                  size="large"
                  onClick={() => { if (window.history.length > 1) window.history.back(); else window.close(); }}
                  style={{ width: mobile ? '100%' : 'auto', height: 48, borderRadius: 10, fontWeight: 700, fontSize: 15 }}
                >
                  ✓ Done / बंद करें
                </Button>,
                <Button
                  key="again"
                  type="primary"
                  size="large"
                  onClick={() => { setSubmitted(false); setSubmittedName(''); setSelectedSector(null); form.resetFields(); }}
                  style={{ ...styles.submitBtn, width: mobile ? '100%' : 'auto', height: 48 }}
                >
                  दूसरा फॉर्म भरें
                </Button>,
              ]}
            />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{`
        .bd-form .ant-form-item { margin-bottom: ${mobile ? '10px' : '18px'} !important; }
        .bd-form .ant-form-item-label { padding: 0 0 2px !important; }
        .bd-form .ant-form-item-label > label { height: auto !important; font-size: 14px !important; font-weight: 600 !important; color: #4a2c00 !important; }
        .bd-header-text { word-break: break-word; }
        @media (max-width: 768px) {
          .bd-form input, .bd-form textarea,
          .bd-form .ant-select-selector, .bd-form .ant-input-number-input {
            font-size: 16px !important;
          }
        }
        /* main.css sets a blanket ".ant-row { margin-left/right: -12px }" under
           max-width:992px for the admin dashboard layout; it leaks onto this
           public page's Rows (including the ones Form.Item renders internally)
           and pushes inputs past the card's padding whenever a Row/Form.Item
           here has no gutter of its own to override it. Match that breakpoint
           so the whole affected range is covered, not just our own mobile one. */
        @media (max-width: 992px) {
          .bd-form .ant-row {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
        @media (max-width: 360px) {
          .bd-header-text-om { font-size: 10px !important; }
          .bd-header-text-title { font-size: 15px !important; }
        }
      `}</style>
      <div style={styles.wrapper}>

        {/* Header */}
        <Card style={styles.headerCard} bodyStyle={{ padding: mobile ? '12px 16px 14px' : '24px 32px 28px' }}>
          <div style={{ textAlign: 'center' }}>

            <Text className="bd-header-text bd-header-text-om" style={{ color: '#f5d9a0', fontSize: mobile ? 11 : 12, display: 'block' }}>
              ।। जय गुरु महाराज री सा 🙏
            </Text>

            <Title className="bd-header-text bd-header-text-title" level={mobile ? 4 : 3} style={{ color: '#FFD700', margin: mobile ? '4px 0 2px' : '8px 0 4px', fontFamily: 'serif', lineHeight: 1.25, fontSize: mobile ? 17 : 26 }}>
              बिजनेस एवं प्रोफेशन डायरेक्टरी
            </Title>
            <Text className="bd-header-text" style={{ color: '#f5c842', fontSize: mobile ? 12 : 14, display: 'block', fontWeight: 500 }}>
             समाज के व्यवसायियों एवं प्रोफेशनल्स की जानकारी संकलन हेतु
            </Text>

            {/* <Divider style={{ borderColor: '#d4a84b', margin: mobile ? '10px 0 12px' : '14px 0 14px' }} /> */}

            {/* <Title className="bd-header-text" level={mobile ? 5 : 4} style={{ color: '#fff', margin: mobile ? '0 0 6px' : '0 0 8px', fontWeight: 700, fontSize: mobile ? 16 : 19, letterSpacing: 0.3 }}>
              बिजनेस एवं प्रोफेशन डायरेक्टरी
            </Title>
            <Text className="bd-header-text" style={{ color: '#f5d9a0', fontSize: mobile ? 11 : 12, display: 'block' }}>
              समाज के व्यवसायियों एवं प्रोफेशनल्स की जानकारी संकलन हेतु
            </Text> */}

          </div>
        </Card>

        {/* Form */}
        <Card
          style={styles.formCard}
          bodyStyle={{ padding: mobile ? '18px 16px 28px' : '28px 32px 36px' }}
        >
          <Spin spinning={loading} tip="जमा हो रहा है...">
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
              size={mobile ? 'middle' : 'large'}
              scrollToFirstError
              className="bd-form"
            >

              {/* Section: Personal */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: mobile ? 10 : 14 }}>
                <div style={{ width: 3, height: 16, background: '#7a0000', borderRadius: 2, flexShrink: 0 }} />
                <Text style={{ fontSize: 11, color: '#7a0000', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                  व्यक्तिगत जानकारी
                </Text>
                <div style={{ flex: 1, height: 1, background: '#f0d9a0' }} />
              </div>

              {/* Phone + Profile Photo side by side */}
              <Row gutter={mobile ? [0, 0] : [12, 0]} style={{ alignItems: 'flex-start' }}>
                {/* Phone — full width on mobile, half on desktop */}
                <Col xs={24} sm={cfg.show_profile_photo ? 14 : 12}>
                  <Form.Item
                    label={<span style={styles.label}>WhatsApp / मोबाइल नंबर *</span>}
                    name="phone_no"
                    rules={phoneRules}
                    validateStatus={phoneDuplicate ? 'error' : undefined}
                    help={phoneDuplicate ? 'यह नंबर पहले से दर्ज है।' : undefined}
                  >
                    <Input
                      prefix={<PhoneOutlined style={{ color: phoneDuplicate ? '#ff4d4f' : '#7a0000' }} />}
                      placeholder="10 अंकों का नंबर (6-9 से शुरू)"
                      maxLength={10}
                      style={{ ...styles.input, borderColor: phoneDuplicate ? '#ff4d4f' : '#d4a84b' }}
                      onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }}
                      onChange={() => { if (phoneDuplicate) setPhoneDuplicate(false); }}
                      onBlur={onPhoneBlur}
                      suffix={phoneChecking ? <Spin size="small" /> : null}
                    />
                  </Form.Item>
                </Col>

                {/* Profile Photo — after father's name on mobile, right of phone on desktop */}
                {cfg.show_profile_photo && (
                  <Col xs={24} sm={6} style={{ display: 'flex', flexDirection: 'column', alignItems: mobile ? 'flex-start' : 'center' }}>
                    <span style={{ ...styles.label, fontSize: 12, marginBottom: 4, display: 'block' }}>प्रोफाइल फोटो</span>
                    <Form.Item name="profile_photo" style={{ marginBottom: 0 }}>
                      <PublicPhotoUpload type="profile" circle />
                    </Form.Item>
                  </Col>
                )}

                {/* Full Name */}
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={styles.label}>पूरा नाम *</span>}
                    name="name"
                    rules={[{ required: true }, { validator: validateName('पूरा नाम') }]}
                  >
                    <Input prefix={<UserOutlined style={{ color: '#7a0000' }} />} placeholder="जैसे: रामलाल" style={styles.input} maxLength={100} />
                  </Form.Item>
                </Col>

                {/* Father's Name */}
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={styles.label}>पिता का नाम *</span>}
                    name="father_name"
                    rules={[{ required: true }, { validator: validateName('पिता का नाम') }]}
                  >
                    <Input prefix={<UserOutlined style={{ color: '#7a0000' }} />} placeholder="जैसे: श्री मोहनलाल जी" style={styles.input} maxLength={100} />
                  </Form.Item>
                </Col>

                {/* Optional: Gotra */}
                {cfg.show_gotra && (
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={styles.label}>गोत्र</span>} name="gotra">
                      <Input prefix={<UserOutlined style={{ color: '#7a0000' }} />} placeholder="जैसे: वशिष्ठ" style={styles.input} maxLength={100} />
                    </Form.Item>
                  </Col>
                )}

                {/* Optional: DOB */}
                {cfg.show_dob && (
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={styles.label}>जन्म तिथि</span>} name="dob">
                      <Input type="date" style={styles.input} />
                    </Form.Item>
                  </Col>
                )}

                {/* Optional: Alternate Phone */}
                {cfg.show_alternate_phone && (
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={styles.label}>वैकल्पिक मोबाइल नंबर</span>} name="alternate_phone">
                      <Input prefix={<PhoneOutlined style={{ color: '#7a0000' }} />} placeholder="दूसरा नंबर (optional)" style={styles.input} maxLength={10}
                        onKeyDown={e => { if (!/[0-9]/.test(e.key) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(e.key)) e.preventDefault(); }} />
                    </Form.Item>
                  </Col>
                )}

                {/* Optional: Email */}
                {cfg.show_email && (
                  <Col xs={24} sm={12}>
                    <Form.Item label={<span style={styles.label}>ईमेल</span>} name="email"
                      rules={[{ type: 'email', message: 'सही ईमेल दर्ज करें' }]}>
                      <Input prefix={<span style={{ color: '#7a0000' }}>@</span>} placeholder="email@example.com" style={styles.input} maxLength={255} />
                    </Form.Item>
                  </Col>
                )}

              </Row>

              {/* Section: Business */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: mobile ? 8 : 14, marginBottom: mobile ? 10 : 14 }}>
                <div style={{ width: 3, height: 16, background: '#7a0000', borderRadius: 2, flexShrink: 0 }} />
                <Text style={{ fontSize: 11, color: '#7a0000', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                  व्यवसाय / प्रोफेशन
                </Text>
                <div style={{ flex: 1, height: 1, background: '#f0d9a0' }} />
              </div>

              <Row gutter={mobile ? [0, 0] : [12, 0]}>
                {/* 4. Main Sector */}
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={styles.label}>मुख्य व्यवसाय क्षेत्र *</span>}
                    name="sector"
                    rules={[{ required: true, message: 'कृपया व्यवसाय क्षेत्र चुनें' }]}
                  >
                    <Select
                      placeholder={loadingSectors ? 'लोड हो रहा है...' : 'क्षेत्र चुनें...'}
                      onChange={onSectorChange}
                      showSearch
                      optionFilterProp="children"
                      style={styles.select}
                      size="large"
                      loading={loadingSectors}
                      virtual={false}
                      listHeight={220}
                      getPopupContainer={trigger => trigger.parentNode}
                    >
                      {sectors.map(s => (
                        <Option key={s.id} value={String(s.id)}>{s.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* 5. Sub Sector */}
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={styles.label}>विशेष कार्य / उप-क्षेत्र</span>}
                    name="sub_sector"
                  >
                    <Select
                      placeholder={selectedSector ? 'उप-क्षेत्र चुनें...' : 'पहले मुख्य क्षेत्र चुनें'}
                      disabled={!selectedSector}
                      showSearch
                      optionFilterProp="children"
                      style={styles.select}
                      size="large"
                      virtual={false}
                      listHeight={220}
                      getPopupContainer={trigger => trigger.parentNode}
                    >
                      {subOptions.map(sub => (
                        <Option key={sub.id} value={String(sub.id)}>{sub.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* 9. Firm Name */}
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={styles.label}>फर्म / दुकान / संस्था का नाम *</span>}
                    name="firm_name"
                    rules={[{ required: true, message: 'कृपया फर्म/दुकान का नाम दर्ज करें' }, { validator: validateSafe('फर्म/दुकान का नाम') }]}
                  >
                    <Input
                      prefix={<ShopOutlined style={{ color: '#7a0000' }} />}
                      placeholder="जैसे: राजपुरोहित ट्रेडर्स"
                      style={styles.input}
                      maxLength={200}
                    />
                  </Form.Item>
                </Col>


                {/* 11. Establishment Year */}
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={<span style={styles.label}>स्थापना वर्ष</span>}
                    name="establishment_year"
                    rules={[{ type: 'number', min: 1900, max: new Date().getFullYear(), message: `1900 से ${new Date().getFullYear()} के बीच दर्ज करें` }]}
                  >
                    <InputNumber
                      placeholder="जैसे: 2010"
                      style={{ width: '100%', height: mobile ? 48 : 44, borderColor: '#d4a84b', borderRadius: 8, fontSize: mobile ? 16 : 14 }}
                      min={1900}
                      max={new Date().getFullYear()}
                    />
                  </Form.Item>
                </Col>

                {/* 10. Business Address */}
                <Col xs={24}>
                  <Form.Item
                    label={<span style={styles.label}>व्यावसायिक पता *</span>}
                    name="business_address"
                    rules={[{ required: true, message: 'कृपया व्यावसायिक पता दर्ज करें' }, { validator: validateSafe('पता') }]}
                  >
                    <TextArea
                      placeholder="दुकान/ऑफिस का पूरा पता लिखें..."
                      rows={3}
                      maxLength={500}
                      style={{ borderColor: '#d4a84b', borderRadius: 8, fontSize: mobile ? 16 : 14 }}
                    />
                  </Form.Item>
                </Col>

                {/* Business City / State — below address */}
                <Col xs={24} sm={14}>
                  <Form.Item
                    label={<span style={styles.label}>व्यवसाय का शहर *</span>}
                    name="current_city"
                    rules={[{ required: true, message: 'कृपया व्यवसाय का शहर दर्ज करें' }, { validator: validateName('शहर') }]}
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: '#7a0000' }} />}
                      placeholder="जैसे: हैदराबाद, मुंबई, जयपुर..."
                      style={styles.input}
                      maxLength={100}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={10}>
                  <Form.Item
                    label={<span style={styles.label}>राज्य *</span>}
                    name="state"
                    rules={[{ required: true, message: 'कृपया राज्य दर्ज करें' }, { validator: validateName('राज्य') }]}
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: '#7a0000' }} />}
                      placeholder="जैसे: राजस्थान, तेलंगाना..."
                      style={styles.input}
                      maxLength={100}
                    />
                  </Form.Item>
                </Col>

                {/* About Business */}
                <Col xs={24}>
                  <Form.Item
                    label={<span style={styles.label}>व्यवसाय के बारे में (optional)</span>}
                    name="business_about"
                  >
                    <TextArea
                      placeholder="अपने व्यवसाय, सेवाओं या उत्पादों के बारे में संक्षेप में लिखें..."
                      rows={3}
                      maxLength={600}
                      style={{ borderColor: '#d4a84b', borderRadius: 8, fontSize: mobile ? 16 : 14 }}
                    />
                  </Form.Item>
                </Col>

                {/* Experience, Rank, Post */}
                <Col xs={24} sm={8}>
                  <Form.Item label={<span style={styles.label}>अनुभव (वर्ष) - Experience</span>} name="experience_years">
                    <InputNumber min={0} max={70} placeholder="जैसे: 10" style={{ width: '100%', borderColor: '#d4a84b', borderRadius: 8 }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label={<span style={styles.label}>रैंक / पदवी - Rank (optional)</span>} name="biz_rank">
                    <Input placeholder="जैसे: Gold Partner, Senior Advisor" style={{ borderColor: '#d4a84b', borderRadius: 8, fontSize: mobile ? 16 : 14 }} maxLength={100} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label={<span style={styles.label}>पद / Post (optional)</span>} name="post">
                    <Input placeholder="जैसे: Director, Manager, Proprietor" style={{ borderColor: '#d4a84b', borderRadius: 8, fontSize: mobile ? 16 : 14 }} maxLength={100} />
                  </Form.Item>
                </Col>

                {/* Business Photo Strip — full width after about */}
                {cfg.show_business_photo && (
                  <Col xs={24}>
                    <Form.Item
                      label={<span style={styles.label}>दुकान / व्यवसाय की फोटो (optional)</span>}
                      name="business_photo"
                    >
                      <PublicMultiPhotoUpload />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              {/* Section: Native Location */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: mobile ? 8 : 14, marginBottom: mobile ? 10 : 14 }}>
                <div style={{ width: 3, height: 16, background: '#7a0000', borderRadius: 2, flexShrink: 0 }} />
                <Text style={{ fontSize: 11, color: '#7a0000', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>
                  मूल स्थान (गाँव / जिला)
                </Text>
                <div style={{ flex: 1, height: 1, background: '#f0d9a0' }} />
              </div>

              <Row gutter={mobile ? [0, 0] : [12, 0]}>
                {/* 6. Village */}
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<span style={styles.label}>गाँव *</span>}
                    name="village"
                    rules={[{ required: true }, { validator: validateName('गाँव') }]}
                  >
                    <Input
                      prefix={<HomeOutlined style={{ color: '#7a0000' }} />}
                      placeholder="मूल गाँव"
                      style={styles.input}
                      maxLength={100}
                    />
                  </Form.Item>
                </Col>

                {/* 7. Tehsil */}
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<span style={styles.label}>तहसील *</span>}
                    name="tehsil"
                    rules={[{ required: true }, { validator: validateName('तहसील') }]}
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: '#7a0000' }} />}
                      placeholder="तहसील"
                      style={styles.input}
                      maxLength={100}
                    />
                  </Form.Item>
                </Col>

                {/* 8. District */}
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<span style={styles.label}>जिला *</span>}
                    name="district"
                    rules={[{ required: true }, { validator: validateName('जिला') }]}
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: '#7a0000' }} />}
                      placeholder="जिला"
                      style={styles.input}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Submit */}
              <div style={{ marginTop: 20 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  icon={<SendOutlined />}
                  style={styles.submitBtn}
                >
                  {loading ? 'जमा हो रहा है...' : 'जानकारी जमा करें'}
                </Button>
                <div style={{ textAlign: 'center', marginTop: 14 }}>
                  <Text style={{ fontSize: 12, color: '#aaa' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                    आपकी जानकारी पूर्णतः सुरक्षित है एवं केवल समाज हित में उपयोग होगी।
                  </Text>
                </div>
              </div>

            </Form>
          </Spin>
        </Card>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: mobile ? '14px 8px 20px' : '16px 0 24px' }}>
          <Text style={{ fontSize: 12, color: '#999', display: 'block' }}>
            अखिल भारतीय राजपुरोहित समाज विकास संस्थान
          </Text>
          <Text style={{ fontSize: 11, color: '#bbb', display: 'block', marginTop: 2 }}>
            श्री खेतेश्वर ब्रह्मधाम तीर्थ, आसोतरा &nbsp;|&nbsp; संपर्क: 9166141008
          </Text>
        </div>

      </div>
    </div>
  );
}

export default BusinessDirectoryForm;
